import React, { useState } from "react";
import { useHSE } from "../lib/store";
import { Button } from "../components/ui/button";
import { FileText, Download, Printer, FileSpreadsheet } from "lucide-react";
import { exportCSV, exportXLSX, exportElementPDF } from "../lib/exportUtils";
import { toast } from "sonner";

const REPORTS = [
  { id: "monthly", title: "Monthly HSSE Summary Report", desc: "Leading and lagging KPI overview with trends", store: "monthlyKPI" },
  { id: "site-wise", title: "Site-wise HSSE Report", desc: "Performance across Marvin's reporting areas", store: "incidents" },
  { id: "site-perf", title: "Site Performance Report", desc: "Cross-site benchmarking & ranking", store: "incidents" },
  { id: "legal", title: "Legal Reference Report", desc: "Legal references in use", store: "legalRefs" },
  { id: "legal-insp", title: "Inspection Legal Compliance Report", desc: "Inspections grouped by legal reference", store: "inspections" },
  { id: "legal-act", title: "Action Plan Legal Compliance Report", desc: "Actions tagged with legal references", store: "actions" },
  { id: "incident", title: "Incident Report", desc: "Detailed incident register", store: "incidents" },
  { id: "training", title: "Training Report", desc: "Compliance & expiry status", store: "trainings" },
  { id: "inspection", title: "Inspection Report", desc: "Workplace inspections", store: "inspections" },
  { id: "action", title: "Action Plan Report", desc: "Open & overdue actions", store: "actions" },
  { id: "ncr-sor", title: "NCR / SOR Report", desc: "Non-conformance & site observations", store: "ncrs" },
  { id: "waste", title: "Waste Report", desc: "Environmental performance", store: "waste" },
  { id: "site-map", title: "Site Map Summary Report", desc: "Site map snapshot with risk overlay", store: "settings" },
  { id: "dashboard", title: "Dashboard Summary Report", desc: "Executive dashboard snapshot", store: "incidents" },
];

export default function Reports() {
  const { data, filters, setFilters, siteNames } = useHSE();
  const [generated, setGenerated] = useState(null);

  const generate = (rep) => {
    setGenerated(rep);
    toast.success(`${rep.title} generated`);
  };

  const exportPdf = async () => {
    if (!generated) return;
    await exportElementPDF("report-content", `${generated.id}-report.pdf`);
    toast.success("PDF exported");
  };

  const exportCsv = () => {
    if (!generated) return;
    const rows = data[generated.store] || [];
    if (Array.isArray(rows)) exportCSV(rows, `${generated.id}.csv`);
    toast.success("CSV exported");
  };

  const exportXls = () => {
    if (!generated) return;
    const rows = data[generated.store] || [];
    if (Array.isArray(rows)) exportXLSX(rows, `${generated.id}.xlsx`, generated.title);
    toast.success("Excel exported");
  };

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight text-[#0E2336]">Reports Center</h1>
        <div className="text-sm text-slate-500">Generate, export and print HSSE reports</div>
      </div>

      {/* Filters */}
      <div className="command-card p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Month</label>
            <select value={filters.month} onChange={(e) => setFilters({ ...filters, month: e.target.value })}
              className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white">
              <option value="All">All</option>
              {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => <option key={i} value={String(i)}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Year</label>
            <select value={filters.year} onChange={(e) => setFilters({ ...filters, year: e.target.value })}
              className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white">
              {["2023", "2024", "2025", "2026"].map((y) => <option key={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Site</label>
            <select value={filters.site} onChange={(e) => setFilters({ ...filters, site: e.target.value })}
              className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white">
              <option>All Sites</option>
              {siteNames.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Department</label>
            <select value={filters.department} onChange={(e) => setFilters({ ...filters, department: e.target.value })}
              className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white">
              <option>All Departments</option>
              {data.settings.departments.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {REPORTS.map((r) => (
          <div key={r.id} className="command-card p-4">
            <div className="flex items-start gap-2.5">
              <div className="w-9 h-9 rounded-md flex items-center justify-center"
                style={{ background: "#FFF7E0", color: "#FFB517" }}>
                <FileText className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-[13px] text-[#0E2336]">{r.title}</div>
                <div className="text-[11px] text-slate-500 line-clamp-2 mt-0.5">{r.desc}</div>
              </div>
            </div>
            <Button data-testid={`report-${r.id}-generate`} onClick={() => generate(r)} size="sm"
              className="mt-3 w-full h-8 text-xs gap-1.5 font-semibold" style={{ background: "#183C55", color: "white" }}>
              Generate Report
            </Button>
          </div>
        ))}
      </div>

      {generated && (
        <div className="command-card p-5">
          <div className="flex items-center justify-between mb-4 no-print">
            <div>
              <h2 className="font-heading font-bold text-lg text-[#0E2336]">{generated.title}</h2>
              <div className="text-xs text-slate-500">Generated {new Date().toLocaleString()}</div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="report-print-btn" variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={() => window.print()}>
                <Printer className="w-3.5 h-3.5" /> Print
              </Button>
              <Button data-testid="report-csv-btn" variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={exportCsv}>
                <Download className="w-3.5 h-3.5" /> CSV
              </Button>
              <Button data-testid="report-xlsx-btn" variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={exportXls}>
                <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
              </Button>
              <Button data-testid="report-pdf-btn" size="sm" className="h-8 text-xs gap-1.5 font-semibold" style={{ background: "#FFB517", color: "#183C55" }} onClick={exportPdf}>
                <Download className="w-3.5 h-3.5" /> PDF
              </Button>
            </div>
          </div>

          <div id="report-content" className="bg-white rounded-lg p-4">
            <div className="border-b border-slate-200 pb-4 mb-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#FFB517] font-semibold">HSSE Command & Reporting System</div>
              <h2 className="font-heading text-2xl font-bold text-[#0E2336] mt-1">{generated.title}</h2>
              <div className="text-sm text-slate-500 mt-1">Client: <b>{data.settings.client || "Marvin HSSE Dashboard"}</b> | System: <b>{data.settings.company || "Personal HSSE Reporting System"}</b> | Year {filters.year} | {filters.site}</div>
            </div>
            <ReportBody report={generated} data={data} />
          </div>
        </div>
      )}
    </div>
  );
}

function ReportBody({ report, data }) {
  const rows = Array.isArray(data[report.store]) ? data[report.store] : [];
  if (!Array.isArray(rows) || rows.length === 0) {
    return <div className="text-slate-400 italic py-8 text-center">No data available for this report.</div>;
  }
  const cols = Object.keys(rows[0]).slice(0, 9);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]">
        <thead style={{ background: "#183C55" }}>
          <tr>{cols.map((c) => <th key={c} className="text-left py-2 px-2 text-[10px] uppercase tracking-wider text-white font-semibold">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.slice(0, 50).map((r, i) => (
            <tr key={i} className={i % 2 ? "bg-slate-50" : ""}>
              {cols.map((c) => <td key={c} className="py-1.5 px-2 text-slate-700 border-b border-slate-100">{String(r[c] ?? "")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 50 && <div className="text-[11px] text-slate-400 mt-2">Showing 50 of {rows.length} records</div>}
    </div>
  );
}
